// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useKeycloak } from "../../context/KeycloakProvider";
import {
  Search,
  Package,
  Plus,
  Euro,
  PackageCheck,
  Warehouse,
  ClipboardList,
  Filter,
  ChevronDown,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Download,
  Upload,
} from "lucide-react";
import toast from "react-hot-toast";
import { productService } from "../../services/productService";
import "../../components/admin/Product/Product.css";

import DataTable from "react-data-table-component";
import LoadingOverlay from "../../components/LoadingOverlay";
import InlineNotification from "../../components/InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import ProductDetailView from "../../components/admin/Product/EditProduct";
import AddProductForm from "../../components/admin/Product/AddProductForm";
import MultiSelectFilter from "../../components/admin/Product/MultiSelectFilter";
import EditColumnsModal from "../../components/admin/Product/EditColumnsModal";
import ImportProductsModal from "../../components/admin/Product/ImportProductsModal";
import BulkEditModal from "../../components/admin/Product/BulkEditModal";
import {
  getFilterConfig,
  fetchFilterOptions,
  defaultFilterOptions,
} from "../../components/admin/Product/filterOptions";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import { SCENARIO_SHORT_TRANSLATIONS } from "../../utils/scenarioConfig";
import env from "../../utils/env";
import { useTheme } from "../../context/ThemeContext";

const Products = () => {
  const { getToken } = useKeycloak();
  const { isDarkMode } = useTheme();
  const [currentTab, setCurrentTab] = useState("bookable");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Immediate input value
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // Debounced value for API calls
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Checkbox selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const tabScrollRef = useRef(null);

  const [showFilters, setShowFilters] = useState(true);
  const [openFilter, setOpenFilter] = useState(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [maxVisibleFilters, setMaxVisibleFilters] = useState(3);
  const [_containerWidth, _setContainerWidth] = useState(0);
  const filterContainerRef = useRef(null);

  // Bulk operations
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    actionLabel: "",
    onConfirm: null,
  });
  const [bulkProgress, setBulkProgress] = useState({
    active: false,
    current: 0,
    total: 0,
    message: "",
  });
  const [bulkLog, setBulkLog] = useState([]);
  const [showBulkLog, setShowBulkLog] = useState(false);
  const [undoStack, setUndoStack] = useState(null);

  // Separate counts for each tab
  const [tabCounts, setTabCounts] = useState({
    bookable: 0,
    all: 0,
    preise: 0,
    pakete: 0,
    lager: 0,
  });

  // Separate filter states for each tab
  const [tabFilters, setTabFilters] = useState({
    bookable: {
      location: [],
      status: ["active"],
      type: [],
      product_type: [],
      scenarios: [],
      layout_positions: [],
      paket_options: [],
      applicable_for: [],
    },
    all: {
      location: [],
      status: [],
      type: [],
      product_type: [],
      scenarios: [],
      layout_positions: [],
      paket_options: [],
      applicable_for: [],
    },
    preise: {
      location: [],
      status: [],
      type: [],
      product_type: [],
      scenarios: [],
      layout_positions: [],
      paket_options: [],
      applicable_for: [],
    },
    pakete: {
      location: [],
      status: [],
      type: [],
      product_type: [],
      scenarios: [],
      layout_positions: [],
      paket_options: ["S", "M", "L"],
      applicable_for: [],
    },
    lager: {
      location: [],
      status: [],
      type: [],
      product_type: [],
      scenarios: [],
      layout_positions: [],
      paket_options: [],
      applicable_for: [],
    },
  });

  // Column configuration for each tab (updated to include checkbox column)
  const [tabColumnConfigs, setTabColumnConfigs] = useState({
    bookable: {
      visibleColumns: [
        "checkbox",
        "vhp_name",
        "bruttopreis_pro_einheit",
        "unit",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
        "created_at",
        "vhp_last_modified",
      ],
      frozenColumns: 4,
      columnOrder: [
        "checkbox",
        "vhp_name",
        "bruttopreis_pro_einheit",
        "unit",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
        "created_at",
        "vhp_last_modified",
      ],
      columnWidths: {
        checkbox: 48,
        vhp_name: 250,
        bruttopreis_pro_einheit: 120,
        unit: 120,
        bookable_for_scenarios: 300,
        vhp_archiving_flag: 100,
        created_at: 100,
        vhp_last_modified: 150,
      },
    },
    all: {
      visibleColumns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "type",
        "vhp_product_type",
        "vhp_archiving_flag",
        "vhp_location",
        "created_at",
      ],
      frozenColumns: 2,
      columnOrder: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "type",
        "vhp_product_type",
        "vhp_archiving_flag",
        "vhp_location",
        "created_at",
      ],
      columnWidths: {
        checkbox: 48,
        vhp_name: 250,
        vhp_sku: 120,
        type: 150,
        vhp_product_type: 120,
        vhp_archiving_flag: 100,
        vhp_location: 120,
        created_at: 100,
      },
    },
    preise: {
      visibleColumns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "vhp_archiving_flag",
      ],
      frozenColumns: 2,
      columnOrder: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "vhp_archiving_flag",
      ],
      columnWidths: {
        checkbox: 48,
        vhp_name: 250,
        vhp_sku: 120,
        bruttopreis_pro_einheit: 140,
        vhp_net_price: 120,
        unit: 80,
        vhp_archiving_flag: 100,
      },
    },
    pakete: {
      visibleColumns: [
        "checkbox",
        "vhp_name",
        "paket",
        "layout_position",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
      ],
      frozenColumns: 2,
      columnOrder: [
        "checkbox",
        "vhp_name",
        "paket",
        "layout_position",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
      ],
      columnWidths: {
        checkbox: 48,
        vhp_name: 250,
        paket: 100,
        layout_position: 120,
        bookable_for_scenarios: 150,
        vhp_archiving_flag: 100,
      },
    },
    lager: {
      visibleColumns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "vhp_location",
        "mindestabnahme",
        "maximalmenge",
        "vhp_archiving_flag",
      ],
      frozenColumns: 2,
      columnOrder: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "vhp_location",
        "mindestabnahme",
        "maximalmenge",
        "vhp_archiving_flag",
      ],
      columnWidths: {
        checkbox: 48,
        vhp_name: 250,
        vhp_sku: 120,
        vhp_location: 120,
        mindestabnahme: 140,
        maximalmenge: 140,
        vhp_archiving_flag: 100,
      },
    },
  });

  // Use the custom hook for notifications
  const { notification, showSuccess, showError } = useInlineNotification();

  // Catalog API uses detail.message (and optional field, resource, id, etc.)
  const getApiErrorMessage = (error, fallback) => {
    const d = error?.response?.data?.detail;
    const data = error?.response?.data ?? {};
    let msg =
      (d && typeof d === "object" && d.message ? d.message : null) ||
      data.message ||
      error?.message;
    if (typeof msg !== "string") msg = null;
    return msg || fallback;
  };

  const showErrorAndFocus = (message) => {
    showError(message);
    toast.error(message, { duration: 6000 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Memoize current filters to prevent unnecessary re-renders
  const filters = useMemo(() => {
    return (
      tabFilters[currentTab] || {
        location: [],
        status: [],
        type: [],
        product_type: [],
        scenarios: [],
        layout_positions: [],
        paket_options: [],
        applicable_for: [],
      }
    );
  }, [tabFilters, currentTab]);

  const filterConfig = useMemo(() => {
    return getFilterConfig(currentTab, filterOptions);
  }, [currentTab, filterOptions]);

  const apiParams = useMemo(() => {
    return {
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
      search: debouncedSearchTerm || undefined,
      vhp_location:
        filters.location.length > 0 ? filters.location.join(",") : undefined,
      vhp_archiving_flag:
        filters.status.length > 0 ? filters.status.join(",") : undefined,
      type: filters.type.length > 0 ? filters.type.join(",") : undefined,
      vhp_product_type:
        filters.product_type.length > 0
          ? filters.product_type.join(",")
          : undefined,
      bookable_for_scenarios:
        filters.scenarios.length > 0 ? filters.scenarios.join(",") : undefined,
      applicable_for:
        filters.applicable_for?.length > 0
          ? filters.applicable_for.join(",")
          : undefined,
      layout_position:
        filters.layout_positions.length > 0
          ? filters.layout_positions.join(",")
          : undefined,
      paket:
        filters.paket_options.length > 0
          ? filters.paket_options.join(",")
          : undefined,
    };
  }, [currentPage, pageSize, debouncedSearchTerm, filters]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);

    try {
      let response;
      if (currentTab === "bookable") {
        response = await productService.getBookableProducts(apiParams);
      } else {
        response = await productService.getProducts(apiParams);
      }
      setProducts(response.items || []);
      setTotal(response.total || 0);
      if (response.counts) {
        setTabCounts({
          bookable: response.counts.active || 0,
          all: response.counts.total || 0,
          preise: response.counts.total || 0,
          pakete: response.counts.total || 0,
          lager: response.counts.total || 0,
        });
      }
      setHasInitiallyLoaded(true);
    } catch (err) {
      console.error("Error fetching products:", err);
      showErrorAndFocus(
        getApiErrorMessage(err, "Fehler beim Laden der Produkte"),
      );
      setProducts([]);
      setTotal(0);
      setHasInitiallyLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [currentTab, apiParams]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const filterOptionsData = await fetchFilterOptions();
        setFilterOptions(filterOptionsData);
        setTabCounts({
          bookable: 0,
          all: 0,
          preise: 0,
          pakete: 0,
          lager: 0,
        });

        await fetchProducts();
      } catch (error) {
        console.error("Error initializing data:", error);
        showErrorAndFocus(
          getApiErrorMessage(error, "Fehler beim Laden der Filteroptionen"),
        );
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setProducts([]);
      setTotal(0);
      setLoading(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    if (filterOptions !== defaultFilterOptions) {
      fetchProducts();
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearchTerm,
    filters,
    currentTab,
    fetchProducts,
    filterOptions,
  ]);

  // Clear selected rows when products change
  useEffect(() => {
    setSelectedRows(new Set());
    setSelectAll(false);
  }, [products, currentTab]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportDropdownOpen &&
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [exportDropdownOpen]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newPageSize, page) => {
    setPageSize(newPageSize);
    setCurrentPage(page);
  };

  // Checkbox handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(products.map((product) => product.id));
      setSelectedRows(allIds);
      setSelectAll(true);
    }
  };

  const handleSelectRow = (productId) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(productId)) {
      newSelectedRows.delete(productId);
    } else {
      newSelectedRows.add(productId);
    }
    setSelectedRows(newSelectedRows);
    setSelectAll(
      newSelectedRows.size === products.length && products.length > 0,
    );
  };

  const tabConfigs = {
    bookable: {
      title: "Alle Buchbaren Assets",
      icon: Package,
      count: tabCounts.bookable,
      headers: [
        "Auswahl",
        "Name",
        "Bruttopreis",
        "Einheit",
        "Buchbar für",
        "Status",
        "Erstellt",
        "Zuletzt sync",
        "SKU",
        "Kategorie",
        "Typ",
        "Standort",
        "Nettopreis",
        "Min. Menge",
        "Max. Menge",
        "Anzeigebereich",
        "Paket",
        "Abbauzeit (h)",
        "Aufbauzeit (h)",
        "Anwendbare Gebühren",
        "Besonderheiten",
        "Record ID",
        "Image Url",
      ],
      columns: [
        "checkbox",
        "vhp_name",
        "bruttopreis_pro_einheit",
        "unit",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
        "created_at",
        "vhp_last_modified",
        "vhp_sku",
        "vhp_product_type",
        "type",
        "vhp_location",
        "vhp_net_price",
        "mindestabnahme",
        "maximalmenge",
        "layout_position",
        "paket",
        "applicable_for",
        "description",
        "external_id",
        "vhp_image_url",
      ],
    },
    all: {
      title: "Alle VHP Assets",
      icon: ClipboardList,
      count: tabCounts.all,
      headers: [
        "Auswahl",
        "Name",
        "SKU",
        "Kategorie",
        "Typ",
        "Status",
        "Standort",
        "Erstellt",
        "Bruttopreis",
        "Nettopreis",
        "Einheit",
        "Buchbar für",
        "Min. Menge",
        "Max. Menge",
        "Anzeigebereich",
        "Paket",
        "Abbauzeit (h)",
        "Aufbauzeit (h)",
        "Anwendbare Gebühren",
        "Besonderheiten",
        "Record ID",
        "Image Url",
        "Zuletzt sync",
      ],
      columns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "vhp_product_type",
        "type",
        "vhp_archiving_flag",
        "vhp_location",
        "created_at",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "bookable_for_scenarios",
        "mindestabnahme",
        "maximalmenge",
        "layout_position",
        "paket",
        "applicable_for",
        "description",
        "external_id",
        "vhp_image_url",
        "vhp_last_modified",
      ],
    },
    preise: {
      title: "Preise",
      icon: Euro,
      count: tabCounts.preise,
      headers: [
        "Auswahl",
        "Name",
        "SKU",
        "Bruttopreis",
        "Nettopreis",
        "Einheit",
        "Status",
        "Kategorie",
        "Typ",
        "Standort",
        "Buchbar für",
        "Min. Menge",
        "Max. Menge",
        "Anzeigebereich",
        "Paket",
        "Abbauzeit (h)",
        "Aufbauzeit (h)",
        "Anwendbare Gebühren",
        "Besonderheiten",
        "Record ID",
        "Image Url",
        "Erstellt",
        "Zuletzt sync",
      ],
      columns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "vhp_archiving_flag",
        "vhp_product_type",
        "type",
        "vhp_location",
        "bookable_for_scenarios",
        "mindestabnahme",
        "maximalmenge",
        "layout_position",
        "paket",
        "applicable_for",
        "description",
        "external_id",
        "vhp_image_url",
        "created_at",
        "vhp_last_modified",
      ],
    },
    pakete: {
      title: "Pakete",
      icon: PackageCheck,
      count: tabCounts.pakete,
      headers: [
        "Auswahl",
        "Name",
        "Paket",
        "Anzeigebereich",
        "Buchbar für",
        "Status",
        "SKU",
        "Kategorie",
        "Typ",
        "Standort",
        "Bruttopreis",
        "Nettopreis",
        "Einheit",
        "Min. Menge",
        "Max. Menge",
        "Abbauzeit (h)",
        "Aufbauzeit (h)",
        "Anwendbare Gebühren",
        "Besonderheiten",
        "Record ID",
        "Image Url",
        "Erstellt",
        "Zuletzt sync",
      ],
      columns: [
        "checkbox",
        "vhp_name",
        "paket",
        "layout_position",
        "bookable_for_scenarios",
        "vhp_archiving_flag",
        "vhp_sku",
        "vhp_product_type",
        "type",
        "vhp_location",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "mindestabnahme",
        "maximalmenge",
        "applicable_for",
        "description",
        "external_id",
        "vhp_image_url",
        "created_at",
        "vhp_last_modified",
      ],
    },
    lager: {
      title: "Lagerbestand",
      icon: Warehouse,
      count: tabCounts.lager,
      headers: [
        "Auswahl",
        "Name",
        "SKU",
        "Standort",
        "Min. Menge",
        "Max. Menge",
        "Status",
        "Kategorie",
        "Typ",
        "Bruttopreis",
        "Nettopreis",
        "Einheit",
        "Buchbar für",
        "Anzeigebereich",
        "Paket",
        "Abbauzeit (h)",
        "Aufbauzeit (h)",
        "Anwendbare Gebühren",
        "Besonderheiten",
        "Record ID",
        "Image Url",
        "Erstellt",
        "Zuletzt sync",
      ],
      columns: [
        "checkbox",
        "vhp_name",
        "vhp_sku",
        "vhp_location",
        "mindestabnahme",
        "maximalmenge",
        "vhp_archiving_flag",
        "vhp_product_type",
        "type",
        "bruttopreis_pro_einheit",
        "vhp_net_price",
        "unit",
        "bookable_for_scenarios",
        "layout_position",
        "paket",
        "applicable_for",
        "description",
        "external_id",
        "vhp_image_url",
        "created_at",
        "vhp_last_modified",
      ],
    },
  };

  // Ensure all columns are selected by default for each tab on first load
  const [hasInitializedDefaultColumns, setHasInitializedDefaultColumns] =
    useState(false);

  useEffect(() => {
    if (!hasInitializedDefaultColumns) {
      setTabColumnConfigs((prev) => {
        const updated = { ...prev };
        Object.keys(tabConfigs).forEach((key) => {
          const allColumns = tabConfigs[key]?.columns || [];
          // Ensure checkbox remains first if present
          const orderedColumns = allColumns.includes("checkbox")
            ? ["checkbox", ...allColumns.filter((col) => col !== "checkbox")]
            : allColumns;
          updated[key] = {
            ...prev[key],
            visibleColumns: orderedColumns,
            columnOrder: orderedColumns,
          };
        });
        return updated;
      });
      setHasInitializedDefaultColumns(true);
    }
  }, []);

  const handleFilterChange = (filterType, values) => {
    setTabFilters((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        [filterType]: values,
      },
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Note: Page reset is handled in the debounce effect
  };

  const handleFilterToggle = (filterKey) => {
    setOpenFilter(openFilter === filterKey ? null : filterKey);
  };

  // Handle product name click
  const handleProductNameClick = (product) => {
    setSelectedProduct(product);
    setShowDetailView(true);
    setShowAddProductModal(false);
  };

  // Handle edit product from detail view
  const handleEditProduct = async (productId, updatedProduct) => {
    try {
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, ...updatedProduct } : p)),
      );

      showSuccess("Produkt erfolgreich aktualisiert");
    } catch (error) {
      console.error("Error updating product:", error);
      showErrorAndFocus(
        getApiErrorMessage(error, "Fehler beim Aktualisieren des Produkts"),
      );
    }
  };

  // Handle add new product (rethrow on error so AddProductForm can show error and stay open)
  const handleAddNewProduct = async (newProduct) => {
    const createdProduct = await productService.createProduct(newProduct);
    setProducts((prev) => [createdProduct, ...prev]);
    setTotal((prev) => prev + 1);
    showSuccess("Produkt erfolgreich hinzugefügt");
  };

  // Handle export products (format: "csv" | "xlsx" | "json" | "pdf")
  const handleExportProducts = async (fileType = "csv") => {
    setExportDropdownOpen(false);
    setExportLoading(true);
    try {
      const includeArchived =
        filters.status.includes("archived") || currentTab !== "bookable";
      const apiFilters = {
        vhp_location:
          filters.location.length > 0 ? filters.location.join(",") : undefined,
        vhp_archiving_flag:
          filters.status.length > 0 ? filters.status.join(",") : undefined,
        type: filters.type.length > 0 ? filters.type.join(",") : undefined,
        vhp_product_type:
          filters.product_type.length > 0
            ? filters.product_type.join(",")
            : undefined,
        bookable_for_scenarios:
          filters.scenarios.length > 0
            ? filters.scenarios.join(",")
            : undefined,
        applicable_for:
          filters.applicable_for?.length > 0
            ? filters.applicable_for.join(",")
            : undefined,
        layout_position:
          filters.layout_positions.length > 0
            ? filters.layout_positions.join(",")
            : undefined,
        paket:
          filters.paket_options.length > 0
            ? filters.paket_options.join(",")
            : undefined,
        search: debouncedSearchTerm || undefined,
      };
      Object.keys(apiFilters).forEach((key) => {
        if (apiFilters[key] === undefined) delete apiFilters[key];
      });

      // Scope: export selection if any, otherwise all (filtered)
      const scope = selectedRows.size > 0 ? "current_view" : "all";
      const itemIds =
        selectedRows.size > 0 ? Array.from(selectedRows) : undefined;
      const columnConfig =
        tabColumnConfigs[currentTab] || tabColumnConfigs.bookable;
      const visibleCols = (columnConfig.visibleColumns || []).filter(
        (col) => col !== "checkbox",
      );

      const result = await productService.exportProducts({
        file_type: fileType,
        scope,
        item_ids: itemIds,
        filters: apiFilters,
        include_archived: includeArchived,
        columns: visibleCols.length > 0 ? visibleCols : undefined,
      });

      const extMap = { csv: "csv", xlsx: "xlsx", json: "json" };
      const ext = extMap[fileType] || "csv";
      const defaultFilename = `catalog_export_${new Date().toISOString().split("T")[0]}.${ext}`;

      // Backend may return file_url (redirect to download) or inline blob
      const fileUrl = result?.file_url || result?.download_url;
      const totalItems = result?.total_items ?? result?.total ?? 0;

      if (fileUrl) {
        // Build absolute download URL. Backend returns path like /api/v2/admin/catalog/download/xxx.csv
        // so use only the origin of the API base to avoid duplicating /api/v2
        const apiBase =
          env.REACT_APP_API_HOME_URL || env.REACT_APP_API_BASE_URL || "";
        let downloadUrl;
        if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
          downloadUrl = fileUrl;
        } else if (apiBase) {
          const origin = new URL(apiBase).origin;
          const path = fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`;
          downloadUrl = `${origin}${path}`;
        } else {
          const path = fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`;
          downloadUrl = path;
        }

        let fallbackFilename = fileUrl.split("/").pop() || defaultFilename;
        if (!fallbackFilename.toLowerCase().endsWith(`.${ext}`)) {
          fallbackFilename = defaultFilename;
        }

        const token = getToken();
        try {
          const response = await fetch(downloadUrl, {
            method: "GET",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              Accept: "*/*",
            },
            credentials: "same-origin",
          });

          if (response.ok) {
            const contentDisposition = response.headers.get(
              "content-disposition",
            );
            let filename = fallbackFilename;
            if (
              contentDisposition &&
              contentDisposition.includes("filename=")
            ) {
              const match = contentDisposition.match(
                /filename\s*=\s*"?([^";\n]+)"?/i,
              );
              if (match && match[1]) filename = match[1].trim();
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSuccess(
              `Export erfolgreich! ${totalItems} Produkt${totalItems === 1 ? "" : "e"} wurden exportiert.`,
            );
          } else {
            throw new Error(`Download failed with status: ${response.status}`);
          }
        } catch (downloadError) {
          console.error("Download error:", downloadError);
          showErrorAndFocus(
            getApiErrorMessage(
              downloadError,
              "Fehler beim Herunterladen der Datei. Bitte versuchen Sie es erneut.",
            ),
          );
        }
      } else if (result?.data && result.data instanceof Blob) {
        // Some APIs return the file directly in response.data
        const blob = result.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = defaultFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSuccess(
          `Export erfolgreich! ${totalItems} Produkt${totalItems === 1 ? "" : "e"} wurden exportiert.`,
        );
      } else {
        showErrorAndFocus(
          "Export fehlgeschlagen. Bitte versuchen Sie es erneut.",
        );
      }
    } catch (error) {
      console.error("Error exporting products:", error);
      showErrorAndFocus(
        getApiErrorMessage(error, "Fehler beim Exportieren der Produkte"),
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Helper: run bulk updates with progress and optional undo snapshot
  const runBulkUpdate = async (
    productIds,
    buildPayloadForProduct,
    actionLabel,
    options = {},
  ) => {
    const { snapshotForUndo = false } = options;
    const selectedProducts = products.filter((p) => productIds.includes(p.id));
    const total = selectedProducts.length;
    if (total === 0) return { success: 0, failed: 0 };

    setConfirmDialog((prev) => ({ ...prev, open: false }));

    const previousProducts = snapshotForUndo
      ? selectedProducts.map((p) => ({ ...p }))
      : null;

    setBulkProgress({
      active: true,
      current: 0,
      total,
      message: actionLabel,
    });

    let success = 0;
    const failedIds = [];

    for (let i = 0; i < selectedProducts.length; i++) {
      const product = selectedProducts[i];
      const payload = buildPayloadForProduct(product);
      try {
        await productService.updateProduct(product.id, payload);
        success += 1;
      } catch (err) {
        console.error(`Bulk update failed for product ${product.id}:`, err);
        failedIds.push(product.id);
      }
      setBulkProgress((prev) => ({
        ...prev,
        current: i + 1,
      }));
    }

    setBulkProgress({ active: false, current: 0, total: 0, message: "" });

    const logEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      action: actionLabel,
      count: total,
      success,
      failed: failedIds.length,
      failedIds,
    };
    setBulkLog((prev) => [logEntry, ...prev].slice(0, 50));

    if (snapshotForUndo && previousProducts && success > 0) {
      setUndoStack({
        productIds: previousProducts.map((p) => p.id),
        previousProducts,
      });
    }

    if (failedIds.length > 0) {
      showErrorAndFocus(
        `${actionLabel}: ${success} aktualisiert, ${failedIds.length} fehlgeschlagen`,
      );
    } else {
      showSuccess(
        `${actionLabel}: ${success} Produkt${success === 1 ? "" : "e"} aktualisiert`,
      );
    }

    setSelectedRows(new Set());
    setSelectAll(false);
    await fetchProducts();
    return { success, failed: failedIds.length };
  };

  const handleBulkStatus = (status) => {
    const statusLabels = {
      active: "Aktivieren",
      inactive: "Deaktivieren",
      archived: "Archivieren",
    };
    const label = statusLabels[status] || status;
    setConfirmDialog({
      open: true,
      title: "Status ändern",
      message: `Status für ${selectedRows.size} Produkt${selectedRows.size === 1 ? "" : "e"} auf "${label}" setzen?`,
      actionLabel: label,
      onConfirm: () =>
        runBulkUpdate(
          Array.from(selectedRows),
          () => ({ vhp_archiving_flag: status }),
          `Status: ${label}`,
          { snapshotForUndo: true },
        ),
    });
  };

  const handleBulkEditApply = (payload) => {
    setConfirmDialog({
      open: true,
      title: "Bulk bearbeiten",
      message: `Diese Änderungen auf ${selectedRows.size} Produkt${selectedRows.size === 1 ? "" : "e"} anwenden?`,
      actionLabel: "Anwenden",
      payload,
      onConfirm: (pl) => {
        if (!pl) return;
        runBulkUpdate(Array.from(selectedRows), () => pl, "Bulk bearbeiten", {
          snapshotForUndo: true,
        });
      },
    });
  };

  const handleUndoBulk = async () => {
    if (!undoStack || undoStack.previousProducts.length === 0) return;
    setBulkProgress({
      active: true,
      current: 0,
      total: undoStack.previousProducts.length,
      message: "Rückgängig...",
    });
    let success = 0;
    for (let i = 0; i < undoStack.previousProducts.length; i++) {
      const prev = undoStack.previousProducts[i];
      try {
        const updateData = { ...prev };
        delete updateData.id;
        delete updateData.created_at;
        delete updateData.vhp_last_modified;
        delete updateData.vhp_archiving_date;
        await productService.updateProduct(prev.id, updateData);
        success += 1;
      } catch (err) {
        console.error("Undo update failed for product", prev.id, err);
      }
      setBulkProgress((p) => ({ ...p, current: i + 1 }));
    }
    setBulkProgress({ active: false, current: 0, total: 0, message: "" });
    setUndoStack(null);
    showSuccess(
      `Rückgängig: ${success} Produkt${success === 1 ? "" : "e"} wiederhergestellt`,
    );
    await fetchProducts();
  };

  const handleConfirmDialogConfirm = () => {
    const { onConfirm, payload } = confirmDialog;
    if (onConfirm) {
      onConfirm(payload !== undefined ? payload : null);
    }
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      actionLabel: "",
      payload: null,
      onConfirm: null,
    });
  };

  const handleConfirmDialogCancel = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      actionLabel: "",
      onConfirm: null,
    });
  };

  // Handle delete selected products
  const handleDeleteSelectedProducts = async () => {
    if (selectedRows.size === 0) return;

    const selectedCount = selectedRows.size;
    const confirmMessage =
      selectedCount === 1
        ? "Möchten Sie das ausgewählte Produkt wirklich löschen?"
        : `Möchten Sie die ${selectedCount} ausgewählten Produkte wirklich löschen?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const selectedProductIds = Array.from(selectedRows);

      const deletePromises = selectedProductIds.map(async (productId) => {
        try {
          await productService.deleteProduct(productId);
          return { success: true, productId };
        } catch (error) {
          console.error(`Failed to delete product ${productId}:`, error);
          return { success: false, productId, error };
        }
      });

      const results = await Promise.all(deletePromises);
      const successfulDeletes = results.filter((r) => r.success);
      const failedDeletes = results.filter((r) => !r.success);

      if (failedDeletes.length > 0) {
        console.error("Some deletions failed:", failedDeletes);
        const firstError = failedDeletes[0]?.error;
        const msg = firstError
          ? getApiErrorMessage(
              firstError,
              `${failedDeletes.length} von ${selectedCount} Produkten konnten nicht gelöscht werden`,
            )
          : `${failedDeletes.length} von ${selectedCount} Produkten konnten nicht gelöscht werden`;
        showErrorAndFocus(msg);
        return;
      }

      // Remove deleted products from state
      setProducts((prev) =>
        prev.filter((product) => !selectedRows.has(product.id)),
      );
      setTotal((prev) => prev - successfulDeletes.length);

      // Clear selection
      setSelectedRows(new Set());
      setSelectAll(false);

      // Refresh data to ensure we have the latest state
      await fetchProducts();

      showSuccess(
        `${successfulDeletes.length} Produkt${successfulDeletes.length === 1 ? "" : "e"} erfolgreich gelöscht`,
      );
    } catch (error) {
      console.error("Error deleting products:", error);
      showErrorAndFocus(
        getApiErrorMessage(error, "Fehler beim Löschen der Produkte"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Get current tab's column configuration
  const currentColumnConfig =
    tabColumnConfigs[currentTab] || tabColumnConfigs.bookable;

  const generateFrozenColumnCSS = () => {
    const visibleColumns = currentColumnConfig.visibleColumns;
    const frozenColumns = currentColumnConfig.frozenColumns;
    const columnWidths = currentColumnConfig.columnWidths || {};

    let leftPosition = 0;
    const frozenColumnStyles = {};
    const bg = isDarkMode ? "#1e293b" : "#f8f9fa";
    const border = isDarkMode ? "#475569" : "#dee2e6";

    // Only process the actual frozen columns in their visible order
    const frozenVisibleColumns = visibleColumns.slice(0, frozenColumns);

    frozenVisibleColumns.forEach((columnId, index) => {
      const width = columnWidths[columnId] || 150;

      frozenColumnStyles[columnId] = {
        position: "sticky !important",
        zIndex: "10 !important",
        left: `${leftPosition}px !important`,
        background: `${bg} !important`,
        borderRight:
          index === frozenVisibleColumns.length - 1
            ? `4px solid ${border} !important`
            : "",
        width: `${width}px !important`,
        minWidth: `${width}px !important`,
        maxWidth: `${width}px !important`,
      };

      leftPosition += width;
    });

    return frozenColumnStyles;
  };

  const generateFrozenHeaderCSS = () => {
    const visibleColumns = currentColumnConfig.visibleColumns;
    const frozenColumns = currentColumnConfig.frozenColumns;
    const columnWidths = currentColumnConfig.columnWidths || {};

    if (frozenColumns === 0) return "";

    let leftPosition = 0;
    let headerCSS = "";
    const border = isDarkMode ? "#475569" : "#dee2e6";

    // Only process the actual frozen columns in their visible order
    const frozenVisibleColumns = visibleColumns.slice(0, frozenColumns);

    frozenVisibleColumns.forEach((columnId, index) => {
      const width = columnWidths[columnId] || 150;
      const borderRight =
        index === frozenVisibleColumns.length - 1
          ? `4px solid ${border} !important`
          : "";

      headerCSS += `
        .rdt_TableHeadRow > div:nth-child(${index + 1}) {
          position: sticky !important;
          left: ${leftPosition}px !important;
          z-index: 21 !important;
          border-right: ${borderRight};
          width: ${width}px !important;
          min-width: ${width}px !important;
          max-width: ${width}px !important;
        }
        .rdt_TableBody .rdt_TableRow > div:nth-child(${index + 1}) {
          position: sticky !important;
          left: ${leftPosition}px !important;
          z-index: 10 !important;
          border-right: ${borderRight};
          width: ${width}px !important;
          min-width: ${width}px !important;
          max-width: ${width}px !important;
        }
      `;

      leftPosition += width;
    });

    return headerCSS;
  };

  const generateAllColumnsCSS = () => {
    const visibleColumns = currentColumnConfig.visibleColumns;
    const columnWidths = currentColumnConfig.columnWidths || {};
    let allColumnsCSS = "";

    visibleColumns.forEach((columnId, index) => {
      const width = columnWidths[columnId] || 150;

      allColumnsCSS += `
        .rdt_TableHeadRow > div:nth-child(${index + 1}),
        .rdt_TableBody .rdt_TableRow > div:nth-child(${index + 1}) {
          width: ${width}px !important;
          min-width: ${width}px !important;
          max-width: ${width}px !important;
        }
      `;
    });

    return allColumnsCSS;
  };

  const createColumns = () => {
    const visibleColumns = currentColumnConfig.visibleColumns;
    const frozenColumns = currentColumnConfig.frozenColumns;
    const columnWidths = currentColumnConfig.columnWidths || {};
    const allColumnStyles = generateFrozenColumnCSS();

    return visibleColumns.map((column, index) => {
      const isFrozen = index < frozenColumns;
      const width = columnWidths[column] || 150;

      // Special handling for checkbox column
      if (column === "checkbox") {
        return {
          id: column,
          name: (
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              className="w-4 h-4 border-gray-300 dark:border-slate-500 rounded dark:bg-slate-700 accent-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)]"
            />
          ),
          selector: () => null,
          sortable: false,
          frozen: isFrozen,
          style: allColumnStyles[column] || {
            width: `${width}px !important`,
            minWidth: `${width}px !important`,
            maxWidth: `${width}px !important`,
          },
          cell: (row) => (
            <input
              type="checkbox"
              checked={selectedRows.has(row.id)}
              onChange={() => handleSelectRow(row.id)}
              className="w-4 h-4 border-gray-300 dark:border-slate-500 rounded dark:bg-slate-700 accent-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)]"
            />
          ),
        };
      }

      const headerIndex = tabConfigs[currentTab].columns.indexOf(column);
      const header = tabConfigs[currentTab].headers[headerIndex];

      return {
        id: column,
        name: header,
        selector: (row) => row[column],
        sortable: true,
        frozen: isFrozen,
        style: allColumnStyles[column] || {
          width: `${width}px !important`,
          minWidth: `${width}px !important`,
          maxWidth: `${width}px !important`,
        },
        cell: (row) => (
          <div className="text-sm text-gray-900 dark:text-slate-200 overflow-hidden">
            {column === "vhp_name" ? (
              <button
                onClick={() => handleProductNameClick(row)}
                className="primary-color hover:opacity-80 font-medium hover:underline text-left whitespace-nowrap w-full overflow-hidden text-ellipsis"
              >
                {formatCellValue(row, column)}
              </button>
            ) : (
              formatCellValue(row, column)
            )}
          </div>
        ),
      };
    });
  };

  const handleAddProduct = () => {
    setShowAddProductModal(true);
    setShowDetailView(false);
  };

  const handleCloseModal = () => {
    setShowAddProductModal(false);
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedProduct(null);
  };

  // Refined formatCellValue function - only handles API response fields
  const formatCellValue = (item, column) => {
    let value;

    switch (column) {
      case "vhp_name":
        value = item.vhp_name;
        break;
      case "vhp_sku":
        value = item.vhp_sku;
        break;
      case "vhp_archiving_flag":
        value = item.vhp_archiving_flag;
        break;
      case "type":
        value = item.type;
        break;
      case "vhp_product_type":
        value = item.vhp_product_type;
        break;
      case "vhp_location":
        {
          const raw = item.vhp_location;
          if (
            raw != null &&
            filterOptions &&
            Array.isArray(filterOptions.location)
          ) {
            const match = filterOptions.location.find(
              (loc) =>
                loc.value === raw || loc.id === raw || loc.vhp_code === raw,
            );
            value = match ? match.label : raw;
          } else {
            value = raw;
          }
        }
        break;
      case "bruttopreis_pro_einheit":
        value = item.bruttopreis_pro_einheit
          ? `${item.bruttopreis_pro_einheit} €`
          : "--";
        break;
      case "vhp_net_price":
        value = item.vhp_net_price ? `${item.vhp_net_price} €` : "--";
        break;
      case "unit":
        value = item.unit;
        break;
      case "bookable_for_scenarios":
        if (item.bookable_for_scenarios) {
          // Using centralized scenario translations
          const scenarioMap = SCENARIO_SHORT_TRANSLATIONS;
          value = item.bookable_for_scenarios
            .map((scenario) => {
              if (typeof scenario === "object" && scenario !== null) {
                return (
                  scenarioMap[scenario.name] ||
                  scenario.description ||
                  scenario.name ||
                  scenario
                );
              }
              return scenarioMap[scenario] || scenario;
            })
            .join(", ");
        } else {
          value = "--";
        }
        break;
      case "created_at":
        value = item.created_at
          ? new Date(item.created_at).toLocaleDateString("de-DE")
          : "--";
        break;
      case "vhp_last_modified":
        value = item.vhp_last_modified
          ? new Date(item.vhp_last_modified).toLocaleDateString("de-DE")
          : "--";
        break;
      case "mindestabnahme": {
        const mindest = item.mindestabnahme;
        if (mindest?.configurations?.length > 0) {
          value = mindest.configurations
            .map(
              (config) =>
                `${config.scenario.replace("kultur_", "Kultur ")}: ${config.quantity}x ${config.package}`,
            )
            .join(", ");
        } else if (mindest?.default != null) {
          // Use `!= null` so `0` is displayed (not treated as falsy)
          value = String(mindest.default);
        } else {
          value = "--";
        }
        break;
      }
      case "maximalmenge": {
        const maximal = item.maximalmenge;
        if (maximal?.configurations?.length > 0) {
          value = maximal.configurations
            .map(
              (config) =>
                `${config.scenario.replace("kultur_", "Kultur ")}: ${config.quantity}x ${config.package}`,
            )
            .join(", ");
        } else if (maximal?.default != null) {
          value = String(maximal.default);
        } else {
          value = "--";
        }
        break;
      }
      case "applicable_for":
        value = item.applicable_for || "--";
        break;
      case "description":
        value = item.description || "--";
        break;
      case "external_id":
        value = item.external_id || "--";
        break;
      case "vhp_image_url":
        value = item.vhp_image_url || "--";
        break;
      case "paket":
        {
          const p = item.paket;
          if (Array.isArray(p)) {
            value = p.join(", ");
          } else if (typeof p === "string") {
            if (p.includes(",")) {
              value = p
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .join(", ");
            } else {
              // Split concatenated tokens like "SM" => "S, M"
              value = p.split("").filter(Boolean).join(", ");
            }
          } else {
            value = "--";
          }
        }
        break;
      case "layout_position":
        value = item.layout_position || "--";
        break;
      default:
        value = item[column];
    }

    if (column === "vhp_archiving_flag") {
      const statusMap = {
        active: {
          text: "Aktiv",
          class:
            "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
        },
        inactive: {
          text: "Inaktiv",
          class:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
        },
        archived: {
          text: "Archiviert",
          class:
            "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
        },
        fordeletion: {
          text: "Zur Löschung",
          class: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
        },
      };
      const status = statusMap[value] || {
        text: value,
        class:
          "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300",
      };
      return (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${status.class}`}
        >
          {status.text}
        </span>
      );
    } else if (column === "type") {
      const categoryMap = {
        zelte_ueberdachungen: "Zelte & Überdachungen",
        moebel: "Möbel",
        buehnen_podeste: "Bühnen & Podeste",
        absperrungen_sicherheit: "Absperrungen & Sicherheit",
        technik: "Technik",
        marktstände: "Marktstände",
        dekoration_beschilderung: "Dekoration & Beschilderung",
        versorgung: "Versorgung",
        lagerung_transport: "Lagerung & Transport",
        sonstige_lagerartikel: "Sonstige Lagerartikel",
        aufbau_abbau_service: "Aufbau & Abbau Service",
        projektmanagement: "Projektmanagement",
        technische_services: "Technische Services",
        reinigung_wartung: "Reinigung & Wartung",
        sicherheit_personal: "Sicherheit & Personal",
        transport_logistik: "Transport & Logistik",
        beratung: "Beratung",
        notfall_services: "Notfall Services",
        individuelle_services: "Individuelle Services",
        sonstige_services: "Sonstige Services",
        grundgebuehren: "Grundgebühren",
        servicegebuehren: "Servicegebühren",
        sondernutzungsgebuehren: "Sondernutzungsgebühren",
        genehmigungsgebuehren: "Genehmigungsgebühren",
        sonstige_gebuehren: "Sonstige Gebühren",
        andere: "Andere",
        other: "Sonstige",
        inventory: "Inventar",
        service: "Service",
      };
      return categoryMap[value] || value;
    } else if (column === "vhp_product_type") {
      const typeMap = {
        inventory: "Inventar",
        non_inventory: "Kein-Inventar",
        service: "Service",
        delivery_zone: "Lieferzone",
        intern: "Intern",
        fee: "Gebühr",
        kit: "Kit",
        standard: "Standard",
        other: "Sonstiges",
      };
      return typeMap[value] || value;
    }

    // Ensure we always return a string, never an object
    if (typeof value === "object" && value !== null) {
      console.warn(
        `formatCellValue: Object detected for column ${column}:`,
        value,
      );
      value = JSON.stringify(value);
    }

    return value || "--";
  };

  // Calculate total active filters for current tab
  const totalActiveFilters = Object.values(filters).reduce(
    (sum, filterArray) => {
      if (Array.isArray(filterArray)) {
        return sum + filterArray.length;
      }
      return sum;
    },
    0,
  );

  // Handle column visibility toggle
  const handleColumnToggle = (column) => {
    // Prevent toggling the checkbox column
    if (column === "checkbox") return;

    const currentConfig = tabColumnConfigs[currentTab];
    const isVisible = currentConfig.visibleColumns.includes(column);

    let newVisibleColumns;
    if (isVisible) {
      newVisibleColumns = currentConfig.visibleColumns.filter(
        (col) => col !== column,
      );
    } else {
      newVisibleColumns = [...currentConfig.visibleColumns, column];
    }

    setTabColumnConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        visibleColumns: newVisibleColumns,
      },
    }));
  };

  // Handle frozen columns change
  const handleFrozenColumnsChange = (count) => {
    // Ensure at least the checkbox column is frozen
    const minFrozenColumns = 1;
    const adjustedCount = Math.max(minFrozenColumns, count);

    setTabColumnConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        frozenColumns: adjustedCount,
      },
    }));
  };

  // Handle column reordering
  const handleColumnReorder = (dragIndex, hoverIndex) => {
    // Prevent moving the checkbox column
    if (dragIndex === 0 || hoverIndex === 0) return;

    const currentConfig = tabColumnConfigs[currentTab];
    const newOrder = [...currentConfig.visibleColumns];
    const draggedColumn = newOrder[dragIndex];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, draggedColumn);

    setTabColumnConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        visibleColumns: newOrder,
      },
    }));
  };

  // Handle column width change
  const handleColumnWidthChange = (columnId, newWidth) => {
    setTabColumnConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        columnWidths: {
          ...prev[currentTab].columnWidths,
          [columnId]: newWidth,
        },
      },
    }));
  };

  // Remove all columns except checkbox
  const handleRemoveAllColumns = () => {
    setTabColumnConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        visibleColumns: ["checkbox"],
      },
    }));
  };

  const handleTabChange = useCallback((key) => {
    React.startTransition(() => {
      setCurrentTab(key);
      setSelectedProduct(null);
      setShowDetailView(false);
      setCurrentPage(1);
      // Clear selected rows when changing tabs
      setSelectedRows(new Set());
      setSelectAll(false);
      // Reset loading state for new tab
      setHasInitiallyLoaded(false);

      if (key === "bookable") {
        setTabFilters((prev) => ({
          ...prev,
          [key]: {
            location: [],
            status: ["active"],
            type: [],
            product_type: [],
            scenarios: [],
            layout_positions: [],
            paket_options: [],
            applicable_for: [],
          },
        }));
      } else if (key === "pakete") {
        // Set default Paket filters (S, M, L) when pakete tab is opened
        setTabFilters((prev) => ({
          ...prev,
          [key]: {
            location: [],
            status: [],
            type: [],
            product_type: [],
            scenarios: [],
            layout_positions: [],
            paket_options: ["S", "M", "L"],
            applicable_for: [],
          },
        }));
      } else {
        setTabFilters((prev) => ({
          ...prev,
          [key]: {
            location: [],
            status: [],
            type: [],
            product_type: [],
            scenarios: [],
            layout_positions: [],
            paket_options: [],
            applicable_for: [],
          },
        }));
      }
    });
  }, []);

  const scrollTabs = (direction) => {
    if (tabScrollRef.current) {
      const scrollAmount = 200; // Adjust scroll amount as needed
      const currentScroll = tabScrollRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      tabScrollRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const checkScrollState = () => {
    if (tabScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
      const hasOverflowContent = scrollWidth > clientWidth;

      setHasOverflow(hasOverflowContent);
      setCanScrollLeft(hasOverflowContent && scrollLeft > 0);
      setCanScrollRight(
        hasOverflowContent && scrollLeft < scrollWidth - clientWidth,
      );
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      checkScrollState();
    };

    const scrollElement = tabScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      // Check initial state
      checkScrollState();

      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [currentTab, showDetailView]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure layout has updated
      setTimeout(checkScrollState, 100);
    });

    if (tabScrollRef.current) {
      resizeObserver.observe(tabScrollRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [showDetailView]);

  useEffect(() => {
    const calculateMaxVisibleFilters = () => {
      if (!filterContainerRef.current) return;

      const containerWidth = filterContainerRef.current.offsetWidth;
      const availableWidth = showDetailView
        ? containerWidth - 464
        : containerWidth;

      const filterWidth = 160;
      const padding = 0;
      const maxFilters = Math.max(
        1,
        Math.floor((availableWidth - padding) / filterWidth),
      );
      const totalFilters = Object.keys(filterConfig).length;
      const newMaxVisibleFilters = Math.min(maxFilters, totalFilters);

      setMaxVisibleFilters(newMaxVisibleFilters);
    };

    calculateMaxVisibleFilters();

    const handleResize = () => {
      setTimeout(calculateMaxVisibleFilters, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showDetailView, filterConfig]);

  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-900"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        overflow: "hidden",
        height: "100vh",
      }}
    >
      <style>{generateFrozenHeaderCSS()}</style>
      <style>{generateAllColumnsCSS()}</style>
      <div ref={exportDropdownRef} className="relative">
        <GlobalAdminHeader
          title="Produktverwaltung"
          subtitle="Verwalte VHP Assets, Preise, Pakete und Lagerbestände."
          buttons={[
            {
              text: exportLoading ? "Exportieren..." : "Exportieren",
              icon: (
                <Download
                  className={`w-5 h-5 ${exportLoading ? "animate-spin" : ""}`}
                />
              ),
              disabled: exportLoading,
              dropdown: true,
              dropdownOpen: exportDropdownOpen,
              onDropdownToggle: () =>
                !exportLoading && setExportDropdownOpen((prev) => !prev),
              dropdownItems: [
                {
                  label: "Als CSV exportieren",
                  value: "csv",
                  icon: <Download className="w-4 h-4" />,
                },
                {
                  label: "Als JSON exportieren",
                  value: "json",
                  icon: <Download className="w-4 h-4" />,
                },
                {
                  label: "Als Excel exportieren",
                  value: "xlsx",
                  icon: <Download className="w-4 h-4" />,
                },
              ],
              onDropdownSelect: (value) => handleExportProducts(value),
              dropdownFooter:
                selectedRows.size > 0 ? (
                  <div className="border-t border-gray-100 dark:border-slate-600 px-4 py-2 text-xs text-gray-500 dark:text-slate-400">
                    {selectedRows.size} Zeile(n) ausgewählt → Export nur Auswahl
                  </div>
                ) : null,
              className:
                "flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            },
            {
              text: "Importieren",
              icon: <Upload className="w-5 h-5" />,
              onClick: () => setShowImportModal(true),
              className:
                "flex items-center px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors",
            },
            {
              text: "Produkt hinzufügen",
              icon: (
                <Plus className={`w-5 h-5 ${showDetailView ? "" : "mr-0"}`} />
              ),
              onClick: handleAddProduct,
              className:
                "flex items-center px-3 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 transition-colors",
            },
          ]}
        />
      </div>

      <div
        className="mx-auto px-4 lg:px-4 pt-4 pb-4 w-full"
        style={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          flexShrink: 1,
          alignItems: "stretch",
          minBlockSize: 0,
        }}
      >
        <div
          className="flex gap-4"
          style={{
            display: "flex",
            flexDirection: "row",
            flexGrow: 1,
            flexShrink: 1,
            alignItems: "stretch",
            minBlockSize: 0,
          }}
        >
          <div
            ref={filterContainerRef}
            className={`flex flex-col h-full`}
            style={{
              flexGrow: showDetailView ? 1 : "",
              width: showDetailView ? "calc(100% - 464px)" : "100%",
            }}
          >
            <div className="relative flex items-center">
              {hasOverflow && canScrollLeft && (
                <div
                  className="absolute left-0 z-10 h-full cursor-pointer bg-gradient-to-r from-slate-50 to-transparent dark:from-slate-900 dark:to-transparent"
                  style={{ width: "40px" }}
                >
                  <button
                    type="text"
                    onClick={() => scrollTabs("left")}
                    className="w-full h-full flex items-center justify-center"
                    style={{ transform: "translateX(-8px)" }}
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  </button>
                </div>
              )}

              {/* Right Arrow - show when there's overflow and can scroll right */}
              {hasOverflow && canScrollRight && (
                <div
                  className="absolute right-0 z-10 h-full cursor-pointer bg-gradient-to-l from-slate-50 to-transparent dark:from-slate-900 dark:to-transparent"
                  style={{ width: "40px" }}
                >
                  <button
                    onClick={() => scrollTabs("right")}
                    className="w-full h-full flex items-center justify-center"
                    style={{ transform: "translateX(8px)" }}
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  </button>
                </div>
              )}

              {/* Tabs Container */}
              <div
                ref={tabScrollRef}
                className="flex gap-3 mb-0 flex-wrap"
                style={{
                  whiteSpace: "nowrap",
                  flexFlow: "nowrap",
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitScrollbar: { display: "none" },
                  paddingLeft: hasOverflow && canScrollLeft ? "0px" : "0",
                  paddingRight: hasOverflow && canScrollRight ? "0px" : "0",
                }}
              >
                {Object.entries(tabConfigs).map(([key, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handleTabChange(key)}
                      className={`flex items-center px-3 text-base py-2 rounded-lg transition-colors h-full ${
                        currentTab === key
                          ? "bg-primary-color text-white"
                          : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                      }`}
                    >
                      <IconComponent className="w-4 h-4 mr-2" />
                      {config.title}
                      {key === "bookable" && config.count && (
                        <span
                          className={`ml-1 ${currentTab === key ? "text-white" : "text-gray-400 dark:text-slate-400"}`}
                        >
                          ({config.count})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Content */}
            <div
              className="bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:border dark:border-slate-700 p-4 mt-4"
              style={{ height: "calc(100% - 56px)" }}
            >
              <div className="space-y-6 h-full">
                {/* Reusable Notification Component */}
                <InlineNotification notification={notification} />

                {/* Search and Filter Bar */}
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500" />
                      <input
                        type="text"
                        placeholder="Produkte suchen..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                      />
                    </div>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 dark:text-slate-200"
                    >
                      <Filter className="w-5 h-5 mr-2" />
                      Filter
                      <ChevronDown
                        className={`w-4 h-4 ml-2 transition-transform ${showFilters ? "rotate-180" : ""}`}
                      />
                      {totalActiveFilters > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs bg-primary-color-tint primary-color rounded-full">
                          {totalActiveFilters}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setShowColumnModal(true)}
                      className="flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200"
                    >
                      <Settings
                        className={`w-4 h-4 ${showDetailView ? "" : "mr-2"}`}
                      />
                      {showDetailView ? "" : "Spalten bearbeiten"}
                    </button>
                  </div>

                  {/* Multi-Select Filter Dropdowns */}
                  {showFilters && (
                    <div style={{ marginTop: "1rem" }}>
                      <div className="flex items-center gap-2 flex-wrap relative">
                        {/* Visible Filters */}
                        {Object.entries(filterConfig)
                          .slice(0, maxVisibleFilters)
                          .map(([key, config]) => {
                            if (!config) return null;
                            return (
                              <div key={key} className="flex-shrink-0">
                                <MultiSelectFilter
                                  label={config.label}
                                  options={config.options || []}
                                  selectedValues={filters[key] || []}
                                  onSelectionChange={(values) => {
                                    handleFilterChange(key, values);
                                  }}
                                  isOpen={openFilter === key}
                                  onToggle={handleFilterToggle}
                                  filterKey={key}
                                />
                              </div>
                            );
                          })}

                        {/* More Button - Show when there are hidden filters */}
                        {Object.entries(filterConfig).length >
                          maxVisibleFilters && (
                          <div className="flex-shrink-0">
                            <button
                              onClick={() =>
                                setShowMoreFilters(!showMoreFilters)
                              }
                              style={{ lineHeight: "normal" }}
                              className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors flex items-center gap-2 ${
                                showMoreFilters
                                  ? "bg-primary-color-tint border-primary-color primary-color"
                                  : "bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600"
                              }`}
                            >
                              More
                              {(() => {
                                const hiddenActiveCount = Object.entries(
                                  filterConfig,
                                )
                                  .slice(maxVisibleFilters)
                                  .reduce((sum, [key]) => {
                                    const filterArray = filters[key] || [];
                                    return (
                                      sum +
                                      (Array.isArray(filterArray)
                                        ? filterArray.length
                                        : 0)
                                    );
                                  }, 0);
                                return hiddenActiveCount > 0 ? (
                                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-color-tint primary-color rounded-full">
                                    {hiddenActiveCount}
                                  </span>
                                ) : null;
                              })()}
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  showMoreFilters ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {/* More Filters Popover */}
                            {showMoreFilters && (
                              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-50">
                                <div className="p-4">
                                  <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-3">
                                    More filters
                                  </h3>
                                  <div className="flex">
                                    {Object.entries(filterConfig)
                                      .slice(maxVisibleFilters)
                                      .map(([key, config]) => {
                                        if (!config) return null;
                                        return (
                                          <div
                                            key={key}
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            <MultiSelectFilter
                                              label={config.label}
                                              options={config.options || []}
                                              selectedValues={
                                                filters[key] || []
                                              }
                                              onSelectionChange={(values) => {
                                                handleFilterChange(key, values);
                                              }}
                                              isOpen={openFilter === key}
                                              onToggle={handleFilterToggle}
                                              filterKey={key}
                                            />
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Clear All Button */}
                        {totalActiveFilters > 0 && (
                          <button
                            onClick={() => {
                              // Clear all filters for current tab
                              setTabFilters((prev) => ({
                                ...prev,
                                [currentTab]: {
                                  location: [],
                                  status:
                                    currentTab === "bookable" ? ["active"] : [],
                                  type: [],
                                  product_type: [],
                                  scenarios: [],
                                  layout_positions: [],
                                  paket_options: [],
                                  applicable_for: [],
                                },
                              }));
                              setCurrentPage(1);
                            }}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-md transition-colors"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bulk progress indicator */}
                {bulkProgress.active && (
                  <div
                    className="bg-primary-color-tint border border-primary-color rounded-lg py-3 px-4 flex items-center gap-4"
                    style={{ marginTop: "10px" }}
                  >
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-color border-t-transparent" />
                    <span className="text-sm font-medium primary-color">
                      {bulkProgress.message}: {bulkProgress.current} /{" "}
                      {bulkProgress.total}
                    </span>
                    <div className="flex-1 h-2 bg-primary-color-tint rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-color transition-all duration-300"
                        style={{
                          width: `${bulkProgress.total ? (100 * bulkProgress.current) / bulkProgress.total : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Selection / Bulk actions toolbar */}
                {selectedRows.size > 0 && !bulkProgress.active && (
                  <div
                    className="bg-primary-color-tint border border-primary-color rounded-lg py-3 px-4"
                    style={{ marginTop: "10px" }}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm font-medium text-gray-800 dark:text-slate-200">
                        {selectedRows.size} Produkt
                        {selectedRows.size === 1 ? "" : "e"} ausgewählt
                      </p>
                      <div className="h-4 w-px bg-primary-color opacity-60" />
                      <span className="text-xs font-medium text-gray-600 dark:text-slate-400 uppercase">
                        Status:
                      </span>
                      <button
                        onClick={() => handleBulkStatus("active")}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors flex items-center gap-1"
                      >
                        Aktivieren
                      </button>
                      <button
                        onClick={() => handleBulkStatus("inactive")}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 transition-colors flex items-center gap-1"
                      >
                        Deaktivieren
                      </button>
                      <button
                        onClick={() => handleBulkStatus("archived")}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors flex items-center gap-1"
                      >
                        Archivieren
                      </button>
                      <div className="h-4 w-px bg-primary-color opacity-60" />
                      <button
                        onClick={() => setShowBulkEditModal(true)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-color-tint primary-color hover:opacity-90 transition-colors flex items-center gap-1"
                      >
                        <Settings className="w-4 h-4" />
                        Bulk bearbeiten
                      </button>
                      <button
                        onClick={handleDeleteSelectedProducts}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Löschen
                      </button>
                      {undoStack && (
                        <>
                          <div className="h-4 w-px bg-primary-color opacity-60" />
                          <button
                            onClick={handleUndoBulk}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-1"
                          >
                            Rückgängig (letzte Aktion)
                          </button>
                        </>
                      )}
                      {bulkLog.length > 0 && (
                        <>
                          <div className="h-4 w-px bg-primary-color opacity-60" />
                          <button
                            type="button"
                            onClick={() => setShowBulkLog((prev) => !prev)}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            Letzte Aktionen ({bulkLog.length})
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Bulk operations log (audit) */}
                {bulkLog.length > 0 && showBulkLog && (
                  <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-600 rounded-lg py-3 px-4 mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                        Letzte Bulk-Aktionen (Audit)
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowBulkLog(false)}
                        className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <ul className="space-y-1.5 max-h-40 overflow-y-auto text-xs">
                      {bulkLog.slice(0, 10).map((entry) => (
                        <li
                          key={entry.id}
                          className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-slate-600 last:border-0"
                        >
                          <span className="text-gray-700 dark:text-slate-300">
                            {new Date(entry.timestamp).toLocaleString("de-DE")}{" "}
                            – {entry.action}: {entry.success}/{entry.count}
                            {entry.failed > 0 && (
                              <span className="text-red-600 dark:text-red-400">
                                {" "}
                                ({entry.failed} fehlgeschlagen)
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Data Table */}
                <div
                  className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 overflow-hidden relative"
                  style={{
                    height: showFilters
                      ? selectedRows.size > 0
                        ? "calc(100% - 167px)"
                        : "calc(100% - 111px)"
                      : selectedRows.size > 0
                        ? "calc(100% - 119px)"
                        : "calc(100% - 59px)",
                    marginTop: "1rem",
                  }}
                >
                  {/* Same overlay for all loading; z-30 so it covers frozen columns (Name, checkbox) which use z-10/z-21 */}
                  {loading ? (
                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95 dark:bg-slate-900/95">
                      <LoadingOverlay message="Lade Produkte..." />
                    </div>
                  ) : null}
                  <div style={{ height: "100%" }}>
                    <DataTable
                      columns={createColumns()}
                      data={products}
                      keyField="id"
                      className="table-title-style"
                      pagination
                      paginationServer
                      paginationTotalRows={total}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[25, 50, 75, 100]}
                      paginationDefaultPage={currentPage}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handleRowsPerPageChange}
                      noDataComponent={
                        <span className="text-gray-500 dark:text-slate-400">
                          {!hasInitiallyLoaded
                            ? "Lade Produkte..."
                            : "Keine Produkte gefunden"}
                        </span>
                      }
                      progressPending={false}
                      customStyles={{
                        table: {
                          style: {
                            backgroundColor: isDarkMode ? "#1e293b" : undefined,
                          },
                        },
                        headRow: {
                          style: {
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            backgroundColor: isDarkMode ? "#334155" : "#f8f9fa",
                            borderBottom: isDarkMode
                              ? "1px solid #475569"
                              : "1px solid #dee2e6",
                          },
                        },
                        headCells: {
                          style: {
                            fontSize: "13px",
                            fontWeight: "600",
                            color: isDarkMode ? "#94a3b8" : "#374151",
                            backgroundColor: isDarkMode ? "#334155" : "#f8f9fa",
                            borderBottom: isDarkMode
                              ? "1px solid #475569"
                              : "1px solid #dee2e6",
                          },
                        },
                        rows: {
                          style: {
                            backgroundColor: isDarkMode ? "#1e293b" : undefined,
                            "&:hover": {
                              backgroundColor: isDarkMode
                                ? "#334155"
                                : undefined,
                            },
                          },
                        },
                        cells: {
                          style: {
                            fontSize: "13px",
                            color: isDarkMode ? "#e2e8f0" : "#374151",
                            backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
                            borderBottom: isDarkMode
                              ? "1px solid #334155"
                              : "1px solid #f1f3f4",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Product Detail View */}
          {showDetailView && (
            <div className="flex-shrink-0" style={{ width: "440px" }}>
              <ProductDetailView
                product={selectedProduct}
                isOpen={showDetailView}
                onClose={handleCloseDetailView}
                onEdit={handleEditProduct}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add Product Form */}
      <AddProductForm
        isOpen={showAddProductModal}
        onClose={handleCloseModal}
        onSave={handleAddNewProduct}
      />

      <ImportProductsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={fetchProducts}
      />

      {/* Bulk edit modal */}
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        onApply={handleBulkEditApply}
        selectedCount={selectedRows.size}
        filterOptions={filterOptions}
      />

      {/* Confirmation dialog for bulk operations */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 dark:bg-black/70"
            onClick={handleConfirmDialogCancel}
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl dark:border dark:border-slate-700 max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">
              {confirmDialog.title}
            </h3>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {confirmDialog.message}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleConfirmDialogCancel}
                className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleConfirmDialogConfirm}
                className="px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90"
              >
                {confirmDialog.actionLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Columns Modal */}
      <EditColumnsModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        currentTab={currentTab}
        tabConfigs={tabConfigs}
        currentColumnConfig={currentColumnConfig}
        onColumnToggle={handleColumnToggle}
        onFrozenColumnsChange={handleFrozenColumnsChange}
        onColumnReorder={handleColumnReorder}
        onColumnWidthChange={handleColumnWidthChange}
        onRemoveAllColumns={handleRemoveAllColumns}
        onApply={({ visibleColumns, frozenColumns, columnWidths }) => {
          // Ensure checkbox is always first and always frozen
          const adjustedVisibleColumns = visibleColumns.includes("checkbox")
            ? visibleColumns
            : ["checkbox", ...visibleColumns];

          const adjustedFrozenColumns = Math.max(1, frozenColumns);

          setTabColumnConfigs((prev) => ({
            ...prev,
            [currentTab]: {
              ...prev[currentTab],
              visibleColumns: adjustedVisibleColumns,
              frozenColumns: adjustedFrozenColumns,
              columnOrder: adjustedVisibleColumns,
              columnWidths: columnWidths || prev[currentTab].columnWidths,
            },
          }));
          setShowColumnModal(false);
        }}
      />
    </div>
  );
};

export default Products;
